import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import * as Sentry from '@sentry/browser';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { AppService } from 'src/app/services/app.service';
import { environment } from 'src/environments/environment';

type HttpMethod =
  | 'get'
  | 'post'
  | 'put'
  | 'patch'
  | 'head'
  | 'delete'
  | 'upload'
  | 'download';

@Injectable()
export class IonicNativeSentryInterceptor implements HttpInterceptor {
  constructor(
    private nativeHttp: HTTP,
    private platform: Platform,
    private appService: AppService,
    private apiService: ApiService
  ) {}

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      !request.url.match(this.apiService.newsFeedUrl) &&
      !request.url.match(this.apiService.adrNewsFeedUrl) &&
      !request.url.match(this.apiService.oilNewsFeedUrl)
    ) {
      request = request.clone({
        headers: request.headers.set(
          'Client-Version',
          this.appService.appVersion
        ),
      });
    }

    // Translation asset request
    if (request.url.match('i18n')) {
      return next.handle(request);
    }

    this.logHttpRequest(request);

    if (!Capacitor.isNativePlatform()) {
      return next.handle(request).pipe(
        map((response) => {
          if (response instanceof HttpResponse) {
            this.logHttpResponseSuccess(response);
          }
          return response;
        }),

        catchError((error) => {
          this.logHttpResponseError(error);
          this.logErrorToSentry(error);
          return throwError(error);
        })
      );
    }

    return from(this.handleNativeRequest(request));
  }

  private async handleNativeRequest(
    request: HttpRequest<any>
  ): Promise<HttpResponse<any>> {
    const headerKeys = request.headers.keys();
    let headers = {};
    // prepare headers for native request
    if (headerKeys && headerKeys.length) {
      headerKeys.map((headerKey) => {
        headers = {
          ...headers,
          [headerKey]: request.headers.get(headerKey),
        };
      });
    }

    try {
      await this.platform.ready();
      const method: HttpMethod = request.method.toLowerCase() as HttpMethod;

      const nativeHttpResponse = await this.nativeHttp.sendRequest(
        request.url,
        {
          method,
          data: request.body,
          headers,
          serializer: 'json',
        }
      );

      let body;
      try {
        body = JSON.parse(nativeHttpResponse.data);
      } catch (error) {
        body = nativeHttpResponse?.data;
      }

      const response = new HttpResponse({
        body,
        status: nativeHttpResponse.status,
        headers: new HttpHeaders(nativeHttpResponse.headers),
        url: nativeHttpResponse.url,
      });

      this.logHttpResponseSuccess(response);
      return Promise.resolve(response);
    } catch (error) {
      if (error?.error) {
        error.error = JSON.parse(error.error);
      } else {
        error = JSON.parse(error);
      }

      this.logHttpResponseError(error);
      this.logErrorToSentry(error);
      return Promise.reject(error);
    }
  }

  private logHttpRequest(request: any): void {
    if (!environment.production) {
      console.log('# HttpRequest');
      console.log(request);
    }
  }

  private logHttpResponseSuccess(response: any): void {
    if (!environment.production) {
      console.log('# HttpResponse success #');
      console.log(response);
    }
  }

  private logHttpResponseError(error: any): void {
    if (!environment.production) {
      console.log('# HttpResponse error #');
      console.log(error);
    }
  }

  private logErrorToSentry(error: HttpErrorResponse): void {
    //Excluded errors
    if (
      (error.url.includes('/SledzenieOdpadow/LokalizacjaKierowcy_Dostepna/') &&
        error.status === 404) ||
      (error.url.includes('/SledzenieOdpadow/GdzieJestRealizator') &&
        error.status === 400) ||
      (error.url.includes('/Uzytkownik/Zaloguj') && error.status === 404)
    ) {
      return;
    }

    if (environment.production) {
      // console.log('SENTRY LOGGED');
      // console.log(error);

      Sentry.captureException({
        appVersion: this.appService.appVersion,
        error,
      });
    }
  }
}
